import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Preferences } from '@capacitor/preferences';
import { useNutzerMandanten } from '@tbitop/oneplatform-api';
import { isCapacitor } from '@tbitop/platform';
import { useEffect, useRef } from 'react';

const STORAGE_KEY = 'push_subscribed_topics';

async function loadSubscribedTopics(): Promise<Set<string>> {
  const { value } = await Preferences.get({ key: STORAGE_KEY });
  if (!value) return new Set();
  try {
    return new Set(JSON.parse(value) as string[]);
  } catch {
    return new Set();
  }
}

async function saveSubscribedTopics(topics: Set<string>): Promise<void> {
  await Preferences.set({ key: STORAGE_KEY, value: JSON.stringify([...topics]) });
}

export const usePushNotifications = () => {
  const { mandanten } = useNutzerMandanten();
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (!isCapacitor() || mandanten.length === 0 || hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    (async () => {
      try {
        const { receive } = await FirebaseMessaging.requestPermissions();
        if (receive !== 'granted') return;

        await FirebaseMessaging.getToken();

        const desiredTopics = new Set(mandanten.map(m => `mandant-${m.id}`));
        const persistedTopics = await loadSubscribedTopics();

        const toSubscribe = [...desiredTopics].filter(t => !persistedTopics.has(t));
        const toUnsubscribe = [...persistedTopics].filter(t => !desiredTopics.has(t));

        if (toSubscribe.length === 0 && toUnsubscribe.length === 0) return;

        await Promise.all(toUnsubscribe.map(topic => FirebaseMessaging.unsubscribeFromTopic({ topic })));
        await Promise.all(toSubscribe.map(topic => FirebaseMessaging.subscribeToTopic({ topic })));

        await saveSubscribedTopics(desiredTopics);
      } catch (error) {
        console.error('[PushNotifications] Initialisation failed:', error);
      }
    })();
  }, [mandanten]);
};
